import { Component, inject } from '@angular/core';
import { BadgeModule } from 'primeng/badge';
import { UserAccessService } from '@/services/app/user-access.service';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { BinderService } from '@/services/api/binder.service';
import { ApprovalsService } from '@/services/api/approvals.service';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SignHistoryService } from '@/services/app/sign-history.service';
import { BinderLockService } from '@/services/app/binder-lock.service';

@Component({
    selector: 'app-binder-lock',
    imports: [
      BadgeModule, 
      ButtonModule, 
      TooltipModule,
      DialogModule,
      CommonModule,
    ],
    template: `
        <p-button [label]="!userAccessService.binderEditAccess() ? 'Editing Locked' : userAccessService.foundersRestrictedAccess() ? 'Limited Access' : 'Editing Unlocked'"  [disabled]="userAccessService.binderEditAccess() && !userAccessService.foundersRestrictedAccess()" [icon]="userAccessService.binderEditAccess() ? 'pi pi-lock-open' : 'pi pi-lock'" tooltipPosition="bottom" [severity]="userAccessService.binderEditAccess() ? 'secondary' : 'danger'" [pTooltip]="unlockTooltipContent" (click)="toggleUnlock()"/>
        <ng-template #unlockTooltipContent>
            <div class="flex align-items-center">
                <span>{{userAccessService.binderEditAccess() ? 'Edit Access Granted' : 'Edit Access Denied'}}</span>
            </div>
        </ng-template>
        <p-dialog header="Lock Status" [modal]="true" [(visible)]="visible" [style]="{ width: '25rem' }">
    <span class="p-text-secondary block mb-1">Binder Access Based On:</span>
    <div class="flex items-center gap-4 mb-4">
        <ul>
            <li *ngFor="let item of getLockedState()">
            <i [class]="item.icon" [style]="item.iconStyle"></i>
            <span class="ml-2">{{ item.item }}</span>
            </li>
        </ul>
    </div>
    <div class="flex justify-end gap-2">
        <p-button label="Cancel" severity="secondary" (click)="visible = false" />
        <p-button [label]="buttonLabelText()" [disabled]="loading || buttonLabelText() === 'Request Access'" (click)="unlock($event)" />
    </div>
</p-dialog> 
    `
})
export class BinderLockComponent {

  userAccessService = inject(UserAccessService);
  binderService = inject(BinderService);
  approvalService = inject(ApprovalsService);
  confirmationService = inject(ConfirmationService);
  signHistoryService = inject(SignHistoryService);
  messageService = inject(MessageService);
  binderLockService = inject(BinderLockService);

  loading = false;
  visible = false;


toggleUnlock() {
  this.visible = !this.visible;
}

getLockedState(): {item: string, icon: string, iconStyle?: string}[] {
  const state = this.approvalService.getStateText();
  const permItems = [
    {
      item: 'Edit Permissions',
      icon: this.binderService.apiEditAccess() ? 'pi pi-check' : 'pi pi-times',
      iconStyle: this.binderService.apiEditAccess() ? 'color: green' : 'color: red',
    }
  ]
  if(state) {
    permItems.push({
      item: state.item,
      icon: state.allowed ? 'pi pi-check' : 'pi pi-times',
      iconStyle: state.allowed ? 'color: green' : 'color: red',
    })
  }
  if(this.userAccessService.foundersRestrictedAccess()) {
    permItems.push({
      item: "Limited Access In Founder's Collection",
      icon: 'pi pi-times',
      iconStyle: 'color: red',
    })
  }

  return permItems;
    
}

buttonLabelText() {
  if(this.approvalService.getBannerState().actions?.includes('Cancel Signing')) {
    return 'Cancel Signing';
  }
  if(this.approvalService.getBannerState().actions?.includes('Cancel Request')) {
    return'Cancel Request';
  }
  return 'Request Access';
}

unlock(event: Event) {
  switch(this.buttonLabelText()) {
    case 'Cancel Signing':
      this.confirmCancelSigning(event);
      break;
    case 'Cancel Request':
      this.confirmCancel(event);
      break;
    case 'Request Access':
      this.requestAccess();
      break;
  }
}

requestAccess() {
  
}

confirmCancelSigning(event: Event) {
  this.confirmationService.confirm({
    target: event.target as EventTarget,
    message: 'This will cancel the current signature request. Are you sure that you want to proceed?',
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptIcon: "none",
    rejectIcon: "none",
    acceptLabel: 'Yes',
    rejectLabel: 'No',
    rejectButtonStyleClass: "p-button-text",
    accept: () => {
      this.cancelSigning()
    },
    reject: () => {
      // this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    }
  });
}

confirmCancel(event: Event) {
  this.confirmationService.confirm({
    target: event.target as EventTarget,
    message: 'This will cancel the current approval request. Are you sure that you want to proceed?',
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptIcon: "none",
    rejectIcon: "none",
    acceptLabel: 'Yes',
    rejectLabel: 'No',
    rejectButtonStyleClass: "p-button-text",
    accept: () => {
      this.cancelRequest()
    },
    reject: () => {
      // this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    }
  });
}

cancelSigning() {
  this.binderLockService.handleSignUnlock(this.binderService.currentBinder().BinderID);
}

cancelRequest() {
  this.loading = true;
  const request = this.approvalService.currentRequest();
  const state = this.approvalService.approvalState();
  if (state === 'signing') {
    this.signHistoryService.cancelSignatureRequest(this.binderService.currentBinder().BinderID);
  } else if (request) {
    this.approvalService.cancelApproval(request.approvalId, request.binderId).subscribe(
      (data: any) => {
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Approval Cancelled' });
        this.userAccessService.setBinderEditAccess(true);
        this.binderService.setStatus(3);
        this.approvalService.cancelRequest();
        this.loading = false;
      },
      (error: any) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Approval Request Failed' });
      }
    );
  }
}

}
